import { z, ZodType } from "zod";

import {
  IncidentWorkItemStatusSchema,
  looseUuid,
  type UUID,
} from "@thalamos/common";

import { AdminReportingFieldKeySchema } from "./adminEditReportingFields.js";
import { FormSectionSchema } from "./FormSection.js";
import { IncidentFormTypeSchema } from "./incidentWorkItem.js";
import { UserOrganisationRoleSchema } from "./organisation.js";

export const AuditTrailEntryResourceTypeValues = [
  "incident",
  "user",
  "organisation",
  "userDirectoryInstance",
  "locationPrecisionOverride",
] as const;
export type AuditTrailEntryResourceType =
  (typeof AuditTrailEntryResourceTypeValues)[number];
export const AuditTrailEntryResourceTypeSchema = z.enum(
  AuditTrailEntryResourceTypeValues,
);

export type AuditTrailResourceUnbranded = {
  resourceType: AuditTrailEntryResourceType;
  resourceId: UUID;
  resourceName?: string;
};

export const AuditTrailResourceSchema = z.object({
  resourceType: AuditTrailEntryResourceTypeSchema,
  resourceId: looseUuid(),
  resourceName: z.string().optional(),
}) satisfies ZodType<AuditTrailResourceUnbranded>;

export type AuditTrailResource = z.infer<typeof AuditTrailResourceSchema>;

export const AuditTrailEntryTypeValues = [
  // Aspire user actions
  "aspireReviewIncident",
  "aspireAcceptIncident",
  "aspireRejectIncident",
  "aspireRequestPolicePresence",
  "aspireNotRequestPolicePresence",

  "officerRecordRequestPolicePresence",

  // Test
  "test",

  // Incident
  "createIncident",
  "getIncident",
  "getMyIncidents",
  "updateIncidentData",
  "getArchiveForms",

  // Share
  "formShared",

  // User
  "userLoginSuccess",
  "userLogout",
  "userUpdate",
  "userOnboard",
  "userDelete",
  "searchUsers",
  "createUser",
  "updateUserProfile",

  // Configuration
  "setOrganisationConfiguration",
  "setOrganisationWorkflowVersion",

  // Transfer
  "transferIncident",
  "resolveTransfer",
  "revokeTransfer",

  // Place of Safety
  "createPlaceOfSafety",

  // End Incident
  "preTriageEndIncident", // Deprecated - use endIncident
  "preEndIncident",
  "finishForm",
  "endIncident",

  // User Directory Instance
  "createUserDirectoryInstance",
  "updateUserDirectoryInstance",
  "deleteUserDirectoryInstance",
  "getUserDirectoryInstanceSupervisorMappings",
  "createUserDirectoryInstanceSupervisorMapping",
  "deleteUserDirectoryInstanceSupervisorMapping",
  "updateUserDirectoryInstanceSupervisorMapping",
  "triggerSyncUsersJob",

  // Admin
  "adminGetIncidents",
  "adminAddIncidentAnonData",
  "adminEditReportingFields",
  "adminRelinkPlaceOfSafety",
  "supervisorGetIncidents",
  "addMembershipToUser",
  "setMembershipsToUser",
  "setSupervisorSyncExemption",

  // Organisation
  "createOrganisation",
  "deleteOrganisation",
  "updateOrganisation",

  // Location Precision Override
  "createLocationPrecisionOverride",
  "updateLocationPrecisionOverride",
  "deleteLocationPrecisionOverride",

  // Reporting
  "incidentReport",
  "supersetDashboardAccessed",
  "powerBiDashboardAccessed",
  "supersetDashboardExported",
] as const;

export type AuditTrailEntryType = (typeof AuditTrailEntryTypeValues)[number];
export const AuditTrailEntryTypeSchema = z.enum(AuditTrailEntryTypeValues);

const BaseAuditTrailEntrySchema = z.object({
  id: looseUuid(),
  timestamp: z.coerce.date(),
  userId: looseUuid().optional(),
  userName: z.string().optional(),
  organisationId: looseUuid().optional(),
  organisationName: z.string().optional(),
  parentTraceId: z.string().optional(),
  sessionId: z.string().optional(),
  codeVersion: z.string(),
  resources: z.array(AuditTrailResourceSchema),
});

export type AuditTrailEntryUnbranded = {
  id: UUID;
  timestamp: Date;

  type: AuditTrailEntryType;
  data: Record<string, unknown>;

  userId?: UUID;
  userName?: string;

  organisationId?: UUID;
  organisationName?: string;

  parentTraceId?: string;
  sessionId?: string;
  codeVersion: string;

  resources: AuditTrailResourceUnbranded[];
};

export type GetAuditTrailEntriesResponse = {
  entries: AuditTrailEntryUnbranded[];
  count: number;
};

export const GetAuditTrailEntriesResponseSchema = z.object({
  entries: z.array(
    BaseAuditTrailEntrySchema.extend({
      type: AuditTrailEntryTypeSchema,
      data: z.record(z.string(), z.unknown()),
    }),
  ),
  count: z.number(),
});

export const PolicePresenceRequestedSchema = z.object({
  hasPolicePresenceRequested: z.object({
    valueLabel: z.string(),
    value: z.literal("yes"),
  }),
  policePresenceRequestedReason: z.object({
    valueLabel: z.string(),
    value: z.enum([
      "immediateRiskToLifeOrOfSeriousHarm",
      "notEnoughStaff",
      "transfer",
      "other",
    ]),
  }),
  furtherReasoning: z
    .object({
      value: z.string(),
    })
    .optional(),
});

export const AuditTrailEntrySchema = z
  .discriminatedUnion("type", [
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.test),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.aspireAcceptIncident),
      data: z
        .object({
          userId: looseUuid(),
          userName: z.string(),
          userEmail: z.string(),
          teamId: looseUuid(),
          teamName: z.string(),
          teamEmail: z.string(),
          organisationId: looseUuid(),
          organisationName: z.string(),
          placeOfSafety: z
            .object({
              id: looseUuid().optional(),
              eMHACuratedPlaceOfSafetyId: looseUuid().optional(),

              name: z.string(),
              address: z.string(),
              postalCode: z.string(),
              odsCode: z.string().optional(),
            })
            .optional(),
          // Whether the patient is confirmed to still be at this place of safety.
          isCurrentPlaceOfSafety: z.boolean().optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.aspireRejectIncident),
      data: z
        .object({
          userId: looseUuid(),
          userName: z.string(),
          userEmail: z.string(),
          teamId: looseUuid(),
          teamName: z.string(),
          teamEmail: z.string(),
          organisationId: looseUuid(),
          organisationName: z.string(),
          reason: z.string(),
          reasonDescription: z.string().optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.updateIncidentData),
      data: z
        .object({
          status: IncidentWorkItemStatusSchema.optional(),
          formType: IncidentFormTypeSchema,
          sectionId: FormSectionSchema.optional(),
          changedFields: z.array(z.string()).optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.aspireRequestPolicePresence,
      ),
      data: z
        .object({
          userId: looseUuid(),
          userName: z.string(),
          userEmail: z.string(),
          teamId: looseUuid().optional(),
          teamName: z.string().optional(),
          teamEmail: z.string().optional(),
          organisationId: looseUuid().optional(),
          organisationName: z.string().optional(),
          policePresenceRequested: PolicePresenceRequestedSchema,
          recordPolicePresenceData: z
            .object({
              hasRemainedAtPlaceOfSafety: z.object({
                valueLabel: z.string(),
                value: z.string(),
              }),
              officerName: z
                .object({ value: z.string() })
                .nullable()
                .optional(),
              reasoning: z.object({ value: z.string() }).nullable().optional(),
            })
            .optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.aspireNotRequestPolicePresence,
      ),
      data: z
        .object({
          userId: looseUuid(),
          userName: z.string(),
          userEmail: z.string(),
          teamId: looseUuid().optional(),
          teamName: z.string().optional(),
          teamEmail: z.string().optional(),
          organisationId: looseUuid().optional(),
          organisationName: z.string().optional(),
          policePresenceRequested: z.object({
            valueLabel: z.string(),
            value: z.enum(["no", "already-left"]),
          }),
          recordPolicePresenceData: z
            .object({
              hasRemainedAtPlaceOfSafety: z.object({
                valueLabel: z.string(),
                value: z.string(),
              }),
            })
            .optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.aspireReviewIncident),
      data: z
        .object({
          userId: looseUuid(),
          userName: z.string(),
          userEmail: z.string(),
          teamId: looseUuid().optional(),
          teamName: z.string().optional(),
          teamEmail: z.string().optional(),
          organisationId: looseUuid().optional(),
          organisationName: z.string().optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.createIncident),
      data: z
        .object({
          isOnBehalfOfSomeoneElse: z.boolean(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.adminGetIncidents),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.adminAddIncidentAnonData),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.adminEditReportingFields),
      data: z
        .object({
          reason: z.string().min(1),
          ccmNumber: z.string().min(1),
          snapshotVersionBefore: z.number(),
          snapshotVersionAfter: z.number(),
          edits: z.array(
            z.object({
              formId: looseUuid(),
              sectionId: FormSectionSchema,
              fieldKey: AdminReportingFieldKeySchema,
              previousValue: z.string().nullable(),
              newValue: z.string(),
            }),
          ),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.adminRelinkPlaceOfSafety),
      data: z
        .object({
          reason: z.string().min(1),
          ccmNumber: z.string().min(1),
          snapshotVersionBefore: z.number(),
          snapshotVersionAfter: z.number(),
          relinks: z.array(
            z.object({
              formId: looseUuid(),
              // The place-of-safety change (null when only the type changed).
              // The previous entry is uncontrolled historical data (often a manual
              // entry), so its fields are tolerated as nullable to avoid an audit
              // write failure on incomplete legacy data.
              // These are `nullish` (allow undefined) so audit rows written before a
              // given field existed still parse - audit data is append-only history.
              previousPlaceOfSafety: z
                .object({
                  id: z.string().nullish(),
                  name: z.string().nullish(),
                  address: z.string().nullish(),
                  postalCode: z.string().nullish(),
                })
                .nullish(),
              newPlaceOfSafety: z
                .object({
                  id: z.string(),
                  name: z.string(),
                  address: z.string(),
                  postalCode: z.string(),
                })
                .nullish(),
              // The place-of-safety type change (null when the type was not changed,
              // undefined on rows written before the type fields existed).
              previousPlaceOfSafetyType: z.string().nullish(),
              newPlaceOfSafetyType: z.string().nullish(),
            }),
          ),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.supervisorGetIncidents),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.addMembershipToUser),
      data: z
        .object({
          addedMembership: z.object({
            organisationId: looseUuid(),
            role: UserOrganisationRoleSchema,
          }),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.setMembershipsToUser),
      data: z
        .object({
          userId: looseUuid(),
          setMemberships: z.array(
            z.object({
              organisationId: looseUuid(),
              role: UserOrganisationRoleSchema,
            }),
          ),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.setSupervisorSyncExemption,
      ),
      data: z
        .object({
          userId: looseUuid(),
          exempt: z.boolean(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.getIncident),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.formShared),
      data: z
        .object({
          status: z
            .literal(IncidentWorkItemStatusSchema.enum["form-shared"])
            .optional(),
          posName: z.string(),
          shareType: z.enum(["email", "shareCode"]),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.userLoginSuccess),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.userLogout),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.getMyIncidents),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.getArchiveForms),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.userUpdate),
      data: z
        .object({
          updatedFields: z.array(
            z.enum(["organisationId", "name", "isOnboarded", "shoulderNumber"]),
          ),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.userOnboard),
      data: z
        .object({
          name: z.string(),
          currentOrganisationId: looseUuid(),
          organisationId: looseUuid(),
          role: UserOrganisationRoleSchema,
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.userDelete),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.updateUserProfile),
      data: z
        .object({
          updatedFields: z.array(z.enum(["name", "email", "shoulderNumber"])),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.transferIncident),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.resolveTransfer),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.searchUsers),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.createUser),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.revokeTransfer),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.preTriageEndIncident),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.endIncident),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.preEndIncident),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.officerRecordRequestPolicePresence,
      ),
      data: z
        .object({
          formType: IncidentFormTypeSchema,
          status: IncidentWorkItemStatusSchema.optional(),
          hasPolicePresenceRequested: z
            .object({
              valueLabel: z.string(),
              value: z.string(),
            })
            .optional(),
          hasRemainedAtPlaceOfSafety: z
            .object({
              valueLabel: z.string(),
              value: z.string(),
            })
            .optional(),
          policePresenceRequestedReason: z
            .object({
              valueLabel: z.string(),
              value: z.string(),
            })
            .optional(),
          officerName: z.object({ value: z.string() }).nullable().optional(),
          reasoning: z.object({ value: z.string() }).nullable().optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.createPlaceOfSafety),
      data: z
        .object({
          formType: IncidentFormTypeSchema,
          status: z
            .literal(IncidentWorkItemStatusSchema.enum["awaiting-arrival"])
            .optional(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.finishForm),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.incidentReport),
      data: z
        .object({
          params: z.object({
            startDate: z.string(),
            endDate: z.string(),
          }),
          resultsCount: z.number(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.setOrganisationConfiguration,
      ),
      data: z.object({ organisationId: looseUuid() }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.setOrganisationWorkflowVersion,
      ),
      data: z.object({ organisationId: looseUuid() }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.createOrganisation),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.updateOrganisation),
      data: z.object({ modifiedFields: z.array(z.string()) }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.deleteOrganisation),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.createUserDirectoryInstance,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum
          .getUserDirectoryInstanceSupervisorMappings,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum
          .createUserDirectoryInstanceSupervisorMapping,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum
          .deleteUserDirectoryInstanceSupervisorMapping,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum
          .updateUserDirectoryInstanceSupervisorMapping,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.updateUserDirectoryInstance,
      ),
      data: z.object({ modifiedFields: z.array(z.string()) }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.deleteUserDirectoryInstance,
      ),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.triggerSyncUsersJob),
      data: z.object({}).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.supersetDashboardAccessed),
      data: z
        .object({
          dashboardId: z.string(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.supersetDashboardExported),
      data: z
        .object({
          exportedAt: z.string(),
          format: z.enum(["pdf", "png"]),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(AuditTrailEntryTypeSchema.enum.powerBiDashboardAccessed),
      data: z
        .object({
          accessedAt: z.string(),
        })
        .strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.createLocationPrecisionOverride,
      ),
      data: z.object({ overrideId: looseUuid() }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.updateLocationPrecisionOverride,
      ),
      data: z.object({ overrideId: looseUuid() }).strict(),
    }),
    BaseAuditTrailEntrySchema.extend({
      type: z.literal(
        AuditTrailEntryTypeSchema.enum.deleteLocationPrecisionOverride,
      ),
      data: z.object({ overrideId: looseUuid() }).strict(),
    }),
  ])
  .brand<"vision-common:AuditTrailEntry">() satisfies ZodType<AuditTrailEntryUnbranded>;

export type AuditTrailEntry = z.infer<typeof AuditTrailEntrySchema>;

export const AuditTrailSearchParamsSchema = z.object({
  fromDate: z.coerce.date(),
  toDate: z.coerce.date(),
  limit: z.number().min(1).max(1000).optional(),
  offset: z.number().min(0).optional(),
  types: z.array(AuditTrailEntryTypeSchema).optional(),
  resourceId: looseUuid().optional(),
  userId: looseUuid().optional(),
});

export type AuditTrailSearchParams = z.infer<
  typeof AuditTrailSearchParamsSchema
>;

/**
 * Audit trail entry types relevant to incident lifecycle activity.
 * Used when showing the incident summary timeline — excludes
 * read-only access events (getIncident, adminGetIncidents, etc.).
 */
export const IncidentActivityAuditTypes = [
  // Incident lifecycle
  AuditTrailEntryTypeSchema.enum.createIncident,
  AuditTrailEntryTypeSchema.enum.formShared,
  AuditTrailEntryTypeSchema.enum.createPlaceOfSafety,
  AuditTrailEntryTypeSchema.enum.preTriageEndIncident,
  AuditTrailEntryTypeSchema.enum.preEndIncident,
  AuditTrailEntryTypeSchema.enum.finishForm,
  AuditTrailEntryTypeSchema.enum.endIncident,

  // Transfer
  AuditTrailEntryTypeSchema.enum.transferIncident,
  AuditTrailEntryTypeSchema.enum.resolveTransfer,
  AuditTrailEntryTypeSchema.enum.revokeTransfer,

  // Police presence
  AuditTrailEntryTypeSchema.enum.officerRecordRequestPolicePresence,

  // Admin corrections
  AuditTrailEntryTypeSchema.enum.adminEditReportingFields,
  AuditTrailEntryTypeSchema.enum.adminRelinkPlaceOfSafety,

  // Aspire (NHS) actions
  AuditTrailEntryTypeSchema.enum.aspireReviewIncident,
  AuditTrailEntryTypeSchema.enum.aspireAcceptIncident,
  AuditTrailEntryTypeSchema.enum.aspireRejectIncident,
  AuditTrailEntryTypeSchema.enum.aspireRequestPolicePresence,
  AuditTrailEntryTypeSchema.enum.aspireNotRequestPolicePresence,
] as const satisfies readonly AuditTrailEntryType[];
